/**
 * The pile of sheets a user builds up before sending them to print.
 *
 * Pure functions only — no map, no store, no network — so the payload
 * translation (which is where a wrong field silently produces the wrong
 * sheet) can be unit tested.
 */
import type { LngLat, Orientation, PageSize } from './printMapGeometry';
import { DEFAULT_SCALE_DENOMINATOR, formatScale, sheetLayout, sheetPolygon } from './printMapGeometry';
import { i18n } from '../../i18n';

const t = (key: string, named: Record<string, unknown> = {}): string => i18n.global.t(key, named);

export type SheetMode = 'single' | 'per_day' | 'whole_route' | 'custom';

export interface SheetOptions {
  pageSize: PageSize;
  orientation: Orientation;
  /** null means "let the server fit a scale to each day". */
  scaleDenominator: number | null;
  projection: string;
  gridCrs: string;
  showGrid: boolean;
  showElevationProfile: boolean;
  showDeclination: boolean;
  showScaleBar: boolean;
  showQrCode: boolean;
  showInfoSheet: boolean;
  showSunTimes: boolean;
  showLegend: boolean;
  hikeDate?: string | null;
  timeZone?: string | null;
}

export interface QueuedSheet {
  key: string;
  title: string;
  center: LngLat;
  bearing: number;
  options: SheetOptions;
  sheetMode: SheetMode;
  /** Human summary shown on the pile card. */
  note?: string | null;
}

export interface PrintContext {
  mapType: string;
  defaultTitle: string;
  sourceType?: string | null;
  sourceId?: string | number | null;
  /** Used only when there is no source record to rebuild geometry from. */
  geometry?: GeoJSON.FeatureCollection | null;
}

export const DEFAULT_OPTIONS: SheetOptions = {
  pageSize: 'a4',
  orientation: 'portrait',
  scaleDenominator: 25000,
  projection: 'web-mercator',
  gridCrs: 'LatLon',
  showGrid: true,
  showElevationProfile: false,
  showDeclination: true,
  showScaleBar: true,
  showQrCode: false,
  showInfoSheet: false,
  // On by default and paired with today's date: almost every walker wants to
  // know when the light goes, and asking them to find the switch first meant
  // most sheets printed without it.
  showSunTimes: true,
  showLegend: true,
  hikeDate: null,
  timeZone: null,
};

/**
 * The option changes that follow from choosing a print mode.
 *
 * Extracted because the page had two copies of this — one in the printMode
 * watcher, one in onMounted — and they had already drifted apart: the mount
 * path omitted the source guard on `showInfoSheet`, and nulled the scale
 * unconditionally where the watcher deliberately preserves the user's Fit or
 * Fixed choice. Two copies of a state transition is one copy too many.
 *
 * Returns the full desired state rather than a patch, so a caller cannot
 * apply half of it.
 */
export interface ModeTransition {
  mode: SheetMode;
  /** Whether a hike or recording sits behind the page. */
  hasSource: boolean;
  /** Current scale; null means "fit to the walk". */
  scaleDenominator: number | null;
  /** Restored when a mode cannot fit a scale for itself. */
  lastFixedDenominator: number;
  showInfoSheet: boolean;
  /**
   * True when the page picked the mode for the user on load. Fitting is then
   * the default, because fitting is the reason that mode exists. A mode the
   * user picks themselves must not silently undo their Fit/Fixed choice.
   */
  initial?: boolean;
}

export function optionsForMode({
  mode,
  hasSource,
  scaleDenominator,
  lastFixedDenominator,
  showInfoSheet,
  initial = false,
}: ModeTransition): { scaleDenominator: number | null; showInfoSheet: boolean } {
  if (mode === 'single') {
    // A hand-framed sheet has no route to fit to, so it has to state a scale.
    // Its info sheet is left alone: nothing about framing by hand decides
    // whether you also want the day list.
    return { scaleDenominator: scaleDenominator ?? lastFixedDenominator, showInfoSheet };
  }

  return {
    scaleDenominator: initial ? null : scaleDenominator,
    // Only a multi-day job has days worth summarising.
    showInfoSheet:    mode === 'per_day' && hasSource,
  };
}

export const DEFAULT_PRINT_MAP_TYPE = 'standard-print-map';

/**
 * An `<input type="date">` value for the calendar day a Date falls on
 * *locally*.
 *
 * The obvious `toISOString().slice(0, 10)` converts to UTC first and is wrong
 * either side of midnight: an evening in Auckland reads as tomorrow, and an
 * afternoon in Los Angeles can still read as today only by luck. Sunrise and
 * sunset are printed for whatever day this names, so being a day out is a
 * wrong sheet, not a cosmetic slip.
 */
export function dateInputValue(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * The print-map identifier for the style the user currently has selected.
 *
 * Takes the style object rather than reaching into the store, so the "which
 * style did they pick" question is answerable in a test. The page used to
 * read it from `store.rootState`, which does not exist on a Vuex store
 * instance — only inside module actions — so the optional chain silently
 * collapsed to the default and every sheet printed in Standard regardless of
 * what the user had chosen.
 */
export function resolvePrintMapType(style: unknown): string {
  const candidate = style as { toPrintMapString?: () => unknown } | null | undefined;
  if (typeof candidate?.toPrintMapString !== 'function') return DEFAULT_PRINT_MAP_TYPE;

  const value = candidate.toPrintMapString();
  return typeof value === 'string' && value.length > 0 ? value : DEFAULT_PRINT_MAP_TYPE;
}

/**
 * Every optional thing a sheet can carry, in the order the panel lists them.
 *
 * Kept here rather than inline in the panel so the collapsed "Advanced
 * settings" summary counts exactly what the expanded list shows — a summary
 * that drifts from its own section is worse than none.
 */
export const SHEET_EXTRAS: Array<{ key: keyof SheetOptions; label: string }> = [
  { key: 'showGrid', get label() { return t('utils.print_sheet_queue.extra_grid'); } },
  { key: 'showElevationProfile', get label() { return t('utils.print_sheet_queue.extra_elevation_profile'); } },
  { key: 'showDeclination', get label() { return t('utils.print_sheet_queue.extra_declination'); } },
  { key: 'showScaleBar', get label() { return t('utils.print_sheet_queue.extra_scale_bar'); } },
  { key: 'showLegend', get label() { return t('utils.print_sheet_queue.extra_legend'); } },
  { key: 'showQrCode', get label() { return t('utils.print_sheet_queue.extra_qr_code'); } },
  { key: 'showInfoSheet', get label() { return t('utils.print_sheet_queue.extra_information_sheet'); } },
  { key: 'showSunTimes', get label() { return t('utils.print_sheet_queue.extra_sunrise_sunset'); } },
];

export function enabledExtras(options: SheetOptions): string[] {
  return SHEET_EXTRAS.filter((extra) => options[extra.key] === true).map((extra) => extra.label);
}

/** "Web Mercator · 6 extras on" — what the collapsed advanced section says. */
export function describeAdvancedOptions(options: SheetOptions, projectionLabel: string): string {
  const count = enabledExtras(options).length;
  if (count === 0) return t('utils.print_sheet_queue.nothing_extra', { projection: projectionLabel });
  return count === 1
    ? t('utils.print_sheet_queue.one_extra_on', { projection: projectionLabel })
    : t('utils.print_sheet_queue.extras_on', { projection: projectionLabel, count });
}

export function pointWkt(center: LngLat): string {
  return `POINT (${center.lng} ${center.lat})`;
}

export function sheetKey(index: number, seed: string): string {
  return `sheet-${seed}-${index}`;
}

function orientationLabel(orientation: string): string {
  if (orientation === 'portrait') return t('utils.print_sheet_queue.portrait');
  if (orientation === 'landscape') return t('utils.print_sheet_queue.landscape');
  return orientation;
}

/** "A4 portrait · 1:25,000 · grid + profile" */
export function describeSheet(sheet: QueuedSheet): string {
  const parts = [
    `${sheet.options.pageSize.toUpperCase()} ${orientationLabel(sheet.options.orientation)}`,
    sheet.options.scaleDenominator ? formatScale(sheet.options.scaleDenominator) : t('utils.print_sheet_queue.scale_to_fit'),
  ];

  const extras: string[] = [];
  if (sheet.options.showGrid) extras.push(t('utils.print_sheet_queue.summary_grid'));
  if (sheet.options.showElevationProfile) extras.push(t('utils.print_sheet_queue.summary_profile'));
  if (sheet.options.showSunTimes) extras.push(t('utils.print_sheet_queue.summary_sun'));
  if (sheet.options.showInfoSheet) extras.push(t('utils.print_sheet_queue.summary_info_sheet'));
  if (extras.length) parts.push(extras.join(' + '));

  if (sheet.sheetMode === 'per_day') parts.push(t('utils.print_sheet_queue.one_sheet_per_day'));
  return parts.join('  ·  ');
}

/**
 * Translate one queued sheet into `PrintMapAttributesInput`.
 *
 * When the sheet has a source record we send `dataTypes`/`dataIds` and let
 * the server rebuild the FeatureCollection: that is the path that tags every
 * feature with its `routeStyle` (so the user's route colours survive), and it
 * keeps a twelve-sheet batch from posting the same multi-megabyte geometry
 * twelve times.
 */
export function buildSheetVariables(sheet: QueuedSheet, context: PrintContext): Record<string, unknown> {
  const options = sheet.options;
  const hasSource = Boolean(context.sourceType && context.sourceId);

  const variables: Record<string, unknown> = {
    mapType: context.mapType,
    title: sheet.title || context.defaultTitle,
    center: pointWkt(sheet.center),
    bearing: sheet.bearing || 0,
    pageSize: options.pageSize,
    orientation: options.orientation,
    gridCrs: options.gridCrs,
    projection: options.projection,
    showGrid: options.showGrid,
    showElevationProfile: options.showElevationProfile,
    showDeclination: options.showDeclination,
    showScaleBar: options.showScaleBar,
    showQrCode: options.showQrCode,
    showInfoSheet: options.showInfoSheet,
    showSunTimes: options.showSunTimes,
    showLegend: options.showLegend,
    sheetMode: sheet.sheetMode,
  };

  // Omitted entirely for an automatic scale: the server then picks one that
  // fits each day, which is the whole point of printing a hike rather than a
  // map area.
  if (options.scaleDenominator) variables.scaleDenominator = options.scaleDenominator;
  if (options.hikeDate) variables.hikeDate = options.hikeDate;
  if (options.timeZone) variables.timeZone = options.timeZone;

  if (hasSource) {
    variables.sourceType = context.sourceType;
    variables.sourceId = String(context.sourceId);
    variables.dataTypes = [context.sourceType];
    variables.dataIds = [String(context.sourceId)];
  } else if (context.geometry) {
    variables.geometry = context.geometry;
  }

  return variables;
}

export function buildBatchVariables(sheets: QueuedSheet[], context: PrintContext): Record<string, unknown>[] {
  return sheets.map((sheet) => buildSheetVariables(sheet, context));
}

/**
 * The job-level settings a single PDF cannot vary page to page.
 *
 * The renderer is handed one page size and the document carries one
 * `@page { size: … }` rule, so page size and orientation are fixed for a whole
 * job. `showGrid` and `showElevationProfile` belong here too, less obviously:
 * both change `frame_rect`, so mixing them would mean pages whose maps are
 * different sizes. The rest are job columns on `print_maps` with nowhere
 * per-page to live.
 *
 * Sheets snapshot the panel at the moment they were added, so a pile really
 * can straddle two of these — the user changed page size halfway through.
 */
export function layoutSignature(options: SheetOptions): string {
  return [
    options.pageSize,
    options.orientation,
    options.projection,
    options.gridCrs,
    options.showGrid,
    options.showElevationProfile,
    options.showDeclination,
    options.showScaleBar,
    options.showQrCode,
    options.showInfoSheet,
    options.showSunTimes,
    options.showLegend,
    options.hikeDate ?? '',
    options.timeZone ?? '',
  ].join('|');
}

/**
 * Split a pile into the fewest jobs that can each be one PDF.
 *
 * Groups keep first-seen order, and sheets keep their order within a group, so
 * the page order of the PDF matches the order they were collected in.
 */
export function groupSheetsByLayout(sheets: QueuedSheet[]): QueuedSheet[][] {
  // Parallel arrays rather than [...map.values()]: the build targets a version
  // that cannot spread a Map iterator, and an ordered array of groups is what
  // we want out of this anyway.
  const keys: string[] = [];
  const groups: QueuedSheet[][] = [];

  sheets.forEach((sheet) => {
    const key = layoutSignature(sheet.options);
    const index = keys.indexOf(key);

    if (index === -1) {
      keys.push(key);
      groups.push([sheet]);
    } else {
      groups[index].push(sheet);
    }
  });

  return groups;
}

/**
 * One group of hand-framed sheets as a single multi-page job.
 *
 * Built on top of `buildSheetVariables` so the source/geometry/option
 * translation stays in exactly one place; only the camera moves from the job
 * onto the pages. The job-level `center` and `bearing` are dropped because the
 * server derives them from the union of the pages — leaving them would pin the
 * whole job to whichever sheet happened to be first.
 */
export function buildCombinedVariables(sheets: QueuedSheet[], context: PrintContext): Record<string, unknown> {
  const variables = buildSheetVariables(sheets[0], context);

  delete variables.center;
  delete variables.bearing;

  return {
    ...variables,
    sheetMode: 'custom',
    pages: sheets.map((sheet) => {
      const page: Record<string, unknown> = {
        center: pointWkt(sheet.center),
        bearing: sheet.bearing || 0,
        title: sheet.title,
      };
      // Omitted rather than sent as null, so the server falls back to the
      // job's scale instead of treating "fit" as a literal zero.
      if (sheet.options.scaleDenominator) page.scaleDenominator = sheet.options.scaleDenominator;
      return page;
    }),
  };
}

/**
 * What the panel actually sends for a pile: one job per layout group.
 *
 * Still an array, so it still goes through `createPrintMaps` and keeps that
 * mutation's per-index partial-failure reporting.
 */
export function buildPileVariables(sheets: QueuedSheet[], context: PrintContext): Record<string, unknown>[] {
  return groupSheetsByLayout(sheets).map((group) => buildCombinedVariables(group, context));
}

/** GeoJSON outline of a queued sheet, for the coverage overlay on the map. */
export function sheetOutline(sheet: QueuedSheet, index: number): GeoJSON.Feature<GeoJSON.Polygon> {
  const layout = sheetLayout({
    pageSize: sheet.options.pageSize,
    orientation: sheet.options.orientation,
    showGrid: sheet.options.showGrid,
    showElevationProfile: sheet.options.showElevationProfile,
  });

  const feature = sheetPolygon({
    center: sheet.center,
    denominator: sheet.options.scaleDenominator ?? DEFAULT_SCALE_DENOMINATOR,
    frameWidthMm: layout.frame.width,
    frameHeightMm: layout.frame.height,
    bearingDegrees: sheet.bearing,
  });

  feature.properties = { key: sheet.key, label: String(index + 1), queued: true };
  return feature;
}

/**
 * Coverage outlines for the pile.
 *
 * Only single sheets get one. A per-day or whole-route job is re-framed by
 * the server on each day's own geometry, so its centre is a placeholder —
 * drawing it put a meaningless box on the map wherever the user happened to
 * be looking.
 */
export function outlineCollection(sheets: QueuedSheet[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: sheets
      .filter((sheet) => sheet.sheetMode === 'single')
      .map((sheet, index) => sheetOutline(sheet, index)),
  };
}

/**
 * Total pages a pile will produce, counting per-day fan-out and info sheets.
 *
 * Counted per job rather than per sheet, because a pile is combined into one
 * document: five hand-framed sheets that each asked for an info sheet produce
 * one info sheet, not five.
 */
export function estimatedPageCount(sheets: QueuedSheet[], dayCount: number): number {
  return groupSheetsByLayout(sheets).reduce((total, group) => {
    const mapPages = group.reduce(
      (pages, sheet) => pages + (sheet.sheetMode === 'per_day' ? Math.max(dayCount, 1) : 1),
      0,
    );
    // showInfoSheet is part of the layout signature, so the group agrees on it.
    return total + mapPages + (group[0].options.showInfoSheet ? 1 : 0);
  }, 0);
}

/** How many PDFs a pile comes out as — more than one only when it straddles layouts. */
export function pileJobCount(sheets: QueuedSheet[]): number {
  return groupSheetsByLayout(sheets).length;
}
